import React from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  ShareAltOutlined,
  CopyOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { Space, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { getUserProfile, selectedCustomerIdSelector } from '@store/common/selectors';
import NewSharing from '@components/common/Sharing/Sharing/NewSharing';
import { DASHBOARD_URL_PATH } from '@utils/constants';
import DeleteSavedDashboardWrapper from '@components/saving/DeleteSavedDashboardWrapper/DeleteSavedDashboardWrapper';
import SavingDashboardMetaWrapper from '@components/saving/SavingDashboardMetaWrapper/SavingDashboardMetaWrapper';
import { ACTIONTYPES } from '@src/components/saving/utils';
import { track } from '@utils';
import {
  CLICK_DELETE_ON_SAVED_DASHBOARD_LIST,
  CLICK_EDIT_ON_SAVED_DASHBOARD_LIST,
  CLICK_SHARE_ON_SAVED_DASHBOARD_LIST,
  CLICK_CLONE_ON_SAVED_DASHBOARD_LIST,
} from '@src/utils/sensor/track';
import { SAVED_DASHBOARDS_TAB_KEY } from '../useGetSavedDashboardsList';
import {
  getBaseUrlByProduct,
  shouldShowDeleteInList,
  shouldShowEditInList,
  PUBLIC_SAVED_DASHBOARDS_KEY,
  PRIVATE_SAVED_DASHBOARDS_KEY,
} from '../utils';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { useCustomerDefaultLandingPageInfo } from '@hooks/useCustomerDefaultLandingPageInfo';

const commonStyles = {
  cursor: 'pointer',
};

const disabledStyles = {
  cursor: 'not-allowed',
  color: '#bbb',
};

const DashboardListOptionsMenu = ({ rowData }) => {
  const { id, isPublic, dashboardType, productName } = rowData;
  const dashboardTypeName = get(dashboardType, ['name'], '');
  const queryClient = useQueryClient();
  const userProfile = useSelector(getUserProfile);
  const enableEdit = shouldShowEditInList(isPublic, rowData, userProfile);
  const enableDelete = shouldShowDeleteInList(isPublic, rowData, userProfile);
  const cid = useSelector(selectedCustomerIdSelector);

  // Check if this dashboard is set as the default landing page
  const landingPagePath = useDefaultLandingPageStore((state) => state.path);
  const isFromCustomerDefault = useDefaultLandingPageStore((state) => state.isFromCustomerDefault);
  const savedDashboardPath = DASHBOARD_URL_PATH[dashboardType?.id] + id;
  const isDefaultLandingPage = landingPagePath?.includes(savedDashboardPath);

  // Determine which type of default this dashboard is
  const isPersonalDefault = isDefaultLandingPage && !isFromCustomerDefault;
  const isCustomerDefaultLandingPage = isDefaultLandingPage && isFromCustomerDefault;

  const { customerDefaultInfo } = useCustomerDefaultLandingPageInfo();
  const isCustomerDefaultForAll =
    customerDefaultInfo?.exists && customerDefaultInfo?.path?.includes(savedDashboardPath);

  // HACK alert
  // All the urls in DASHBOARD_URL have a trailing /
  // The SharingModal component (used in lot of other places) used in NewSharing adds another /
  // leading to "//" in the url shown to the user
  // so slicing the trailing / from the constants
  const baseUrl = getBaseUrlByProduct(productName);
  const savedDashboardUrl =
    baseUrl + (DASHBOARD_URL_PATH[rowData.dashboardType.id] || '').slice(0, -1);

  const invalidateQueries = () => {
    queryClient.invalidateQueries([
      SAVED_DASHBOARDS_TAB_KEY,
      isPublic ? PUBLIC_SAVED_DASHBOARDS_KEY : PRIVATE_SAVED_DASHBOARDS_KEY,
    ]);
  };

  const onShareClick = () => {
    track({ event: CLICK_SHARE_ON_SAVED_DASHBOARD_LIST });
  };

  const onUpdateClick = () => {
    track({ event: CLICK_EDIT_ON_SAVED_DASHBOARD_LIST });
  };

  const onCloneClick = () => {
    track({ event: CLICK_CLONE_ON_SAVED_DASHBOARD_LIST });
  };

  const onDeleteClick = () => {
    track({ event: CLICK_DELETE_ON_SAVED_DASHBOARD_LIST });
  };

  return (
    <Space
      size="large"
      align="end"
      style={{ lineHeight: 2, fontSize: 18, display: 'flex', alignItems: 'center' }}
    >
      {isPublic ? (
        <div onClick={onShareClick}>
          <NewSharing url={savedDashboardUrl} id={id} title={rowData.name} cid={cid}>
            <ShareAltOutlined
              style={{
                ...commonStyles,
              }}
            />
          </NewSharing>
        </div>
      ) : null}
      {
        <div onClick={onUpdateClick}>
          <SavingDashboardMetaWrapper
            id={id}
            dashboard={rowData}
            disabled={!enableEdit}
            onSaveCallback={invalidateQueries}
            type={ACTIONTYPES.UPDATE}
          >
            <EditOutlined
              style={{
                ...commonStyles,
                ...(enableEdit ? {} : disabledStyles),
              }}
            />
          </SavingDashboardMetaWrapper>
        </div>
      }
      {
        <div onClick={onCloneClick}>
          <SavingDashboardMetaWrapper
            id={id}
            dashboard={rowData}
            disabled={!enableEdit}
            onSaveCallback={invalidateQueries}
            type={ACTIONTYPES.CLONE}
          >
            <CopyOutlined
              style={{
                ...commonStyles,
                ...(enableEdit ? {} : disabledStyles),
              }}
            />
          </SavingDashboardMetaWrapper>
        </div>
      }
      {
        <Tooltip
          title={isCustomerDefaultLandingPage ? 'Cannot delete customer default landing page' : ''}
        >
          <div onClick={onDeleteClick}>
            <DeleteSavedDashboardWrapper
              pageMeta={{
                id,
              }}
              goToHome={false}
              onDeleteCallback={invalidateQueries}
              disabled={!enableDelete || isCustomerDefaultLandingPage}
              dashboardTypeName={dashboardTypeName}
              isDefaultLandingPage={isDefaultLandingPage && !isCustomerDefaultLandingPage}
            >
              <DeleteOutlined
                style={{
                  ...commonStyles,
                  ...(enableDelete && !isCustomerDefaultLandingPage ? {} : disabledStyles),
                }}
              />
            </DeleteSavedDashboardWrapper>
          </div>
        </Tooltip>
      }
      {isPersonalDefault && (
        <Tooltip title="Your personal default landing page">
          <HomeOutlined style={{ ...commonStyles, color: '#1890ff' }} />
        </Tooltip>
      )}
      {/* For internal admins: show customer default even if they have a different personal default */}
      {isCustomerDefaultForAll && !isCustomerDefaultLandingPage && (
        <Tooltip title="Conviva set default landing page">
          <HomeOutlined style={{ ...commonStyles, color: '#ccc' }} />
        </Tooltip>
      )}
    </Space>
  );
};

export default DashboardListOptionsMenu;
